import React from 'react';
import PropTypes from 'prop-types';
import AbsolutePosImgs from '../_pages/AbsolutePosImgs/AbsolutePosImgs';
import buyAllCardModel from './buyAllCardModel';
import { money } from '../../utils/offerPricing';

/**
 * The elementary "Download all my photos" (Buy All Digital) card (issue #5218
 * item 6, absorbing #5217). Replaces rendering the option's `buy_all_banner`
 * PNG as the WHOLE card -- that art carried "20% OFF / $39.95" baked in, which
 * kept advertising a dead tier.
 *
 * The art is now the backdrop only. Over it: the student's pose on a phone and
 * a magnet mock (the `buyAllTile` layout through the ONE compositor,
 * AbsolutePosImgs), an offer ribbon, and a bottom strip with the option's REAL
 * price and -- only while a % tier is live -- the discountedPrice()
 * strike-through. All copy comes from buyAllCardModel, so the card reads the
 * same over today's baked-in art and the designer's price-free replacement:
 * the strip covers the lower 32% of the tile, where the old art's baked price
 * AND its BUY NOW button sit (the 22% first cut left the baked "$39.95" peeking),
 * so a family on days 0-14 still sees "$49.94 -> $39.95 / 20% OFF" from the
 * LIVE offer rather than from paint.
 *
 * Tailwind only (it lives in the Tailwind gallery); self-contained so the older
 * favourites screen can host it too.
 */
export default function BuyAllCard({
  option, offer, onClick, label, customer, packageId,
}) {
  const model = buyAllCardModel({ option, offer });
  const art = option?.buyAllBannerUrl;

  return (
    <div className="relative w-full h-full bg-shoob-blue" data-testid="buy-all-card">
      {art && <img src={art} alt="" className="block w-full h-full object-cover" />}

      {/* The composite: pose on the phone + a magnet, positioned over the art. */}
      <div className="absolute inset-0 pointer-events-none">
        {customer && packageId && AbsolutePosImgs(customer, packageId, option, {
          layoutKey: 'buyAllTile', freeMagnets: model.freeMagnets,
        })}
      </div>

      {model.ribbon && (
        <span className="absolute top-2 left-2 z-10 rounded-md bg-red-600 px-2 py-1 text-[11px] font-extrabold uppercase tracking-wide text-white shadow">
          {model.ribbon}
        </span>
      )}

      {/* The one control: the price strip. Solid, so it stays legible over any art. */}
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className="absolute inset-x-0 bottom-0 z-10 flex h-[32%] min-h-[44px] items-center justify-between gap-2 border-0 bg-shoob-blue px-3 text-left text-white"
      >
        <span className="text-xs font-bold leading-tight sm:text-sm">{label}</span>
        <span className="flex items-baseline gap-1.5 whitespace-nowrap">
          {model.wasPrice != null && (
            <s className="text-xs text-white/70">{money(model.wasPrice)}</s>
          )}
          {model.nowPrice != null && (
            <span className="text-base font-extrabold sm:text-lg">{money(model.nowPrice)}</span>
          )}
        </span>
      </button>
    </div>
  );
}

BuyAllCard.propTypes = {
  option: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    buyAllBannerUrl: PropTypes.string,
  }).isRequired,
  offer: PropTypes.shape({
    couponType: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string,
  // The compositor's inputs (AbsolutePosImgs). Omit either to skip the composite.
  // eslint-disable-next-line react/forbid-prop-types
  customer: PropTypes.object,
  packageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

BuyAllCard.defaultProps = {
  offer: null,
  label: 'Download all my photos',
  customer: null,
  packageId: null,
};
